import jsPDF from "jspdf";
import type { TElement, TText } from "platejs";
import type { AssemblyProject } from "../../../common/contexts/SelectionContext";
import {
  BLOCK_CHECKLIST,
  BLOCK_LOG,
  BLOCK_NOTA,
  BLOCK_OTF,
  BLOCK_PLECA,
  BLOCK_TRANSCRIPT,
  type ChecklistBlockElement,
} from "./schema";
import { formatDateLabel, pickDateSource } from "./blocks/dateLabel";
import { sanitizeFilename, type StreamTagResolver } from "./exportTxt";

// A4 portrait en pt (default unit cuando le pasás "pt" a jsPDF).
const PAGE_W = 595.28;
const PAGE_H = 841.89;
const MARGIN = 48;
const CONTENT_W = PAGE_W - MARGIN * 2;
const BOTTOM = PAGE_H - MARGIN;

const BODY_SIZE = 11;
// Negro plano para que el render del PDF coincida con la salida DOC.
// La familia se fija en "helvetica" — es la sans-serif base de jsPDF y los
// visores PDF la sustituyen por Arial al mostrarla; embeber un TTF real de
// Arial sumaría ~200 KB al bundle.
const BODY_COLOR: [number, number, number] = [0, 0, 0];
const FONT_FAMILY = "helvetica";

function nodeText(node: TElement): string {
  return ((node.children ?? []) as TText[])
    .map((c) => (typeof c?.text === "string" ? c.text : ""))
    .join("")
    .trim();
}

interface RenderCtx {
  doc: jsPDF;
  y: number;
}

function ensureSpace(ctx: RenderCtx, needed: number): void {
  if (ctx.y + needed > BOTTOM) {
    ctx.doc.addPage();
    ctx.y = MARGIN;
  }
}

// jsPDF posiciona el texto por baseline, y `drawText` deja `ctx.y` un line-height
// completo por debajo de la última baseline visible. Eso da espacio "fantasma"
// arriba del divisor (la línea cae lejos del texto previo) que NO existe abajo
// (el texto siguiente queda pegado a la línea). Para que la separación visual
// sea simétrica, el padding de abajo tiene que compensar ascent del texto que
// viene, y arriba se reduce porque ya viene con el line-height incluido del
// bloque anterior.
const DIVIDER_PAD_ABOVE = 14;
const DIVIDER_PAD_BELOW = 14 + BODY_SIZE * 1.6;
function drawDivider(ctx: RenderCtx): void {
  ensureSpace(ctx, DIVIDER_PAD_ABOVE + DIVIDER_PAD_BELOW + 4);
  ctx.y += DIVIDER_PAD_ABOVE;
  ctx.doc.setDrawColor(200);
  ctx.doc.setLineWidth(0.5);
  ctx.doc.line(MARGIN, ctx.y, PAGE_W - MARGIN, ctx.y);
  ctx.y += DIVIDER_PAD_BELOW;
}

type FontStyle = "normal" | "bold" | "italic";

function drawText(ctx: RenderCtx, text: string, style: FontStyle = "normal"): void {
  if (!text) return;
  ctx.doc.setFont(FONT_FAMILY, style);
  ctx.doc.setFontSize(BODY_SIZE);
  ctx.doc.setTextColor(...BODY_COLOR);
  const lines = ctx.doc.splitTextToSize(text, CONTENT_W) as string[];
  const lh = BODY_SIZE * 1.35;
  for (const line of lines) {
    ensureSpace(ctx, lh);
    ctx.doc.text(line, MARGIN, ctx.y);
    ctx.y += lh;
  }
}

// Mirror del helper en exportTxt — el PDF muestra la misma señal de origen
// para que los tres exports sean consistentes en pantalla.
function otfLabelWithOrigin(originType: unknown): string {
  if (originType === "log") return "OTF←LOG";
  if (originType === "transcript" || originType === "transcription") return "OTF←TRANS";
  return "OTF";
}

function blockHeaderText(label: string | null, el: any): string {
  const date = formatDateLabel(pickDateSource(el));
  const speaker = el.speakerName ? String(el.speakerName) : "";
  // LOG/TRANSCRIPT pasan label=null para que se vean igual y colapsen juntos
  // (ver groupKey). OTF sí muestra label porque marca el origen de la
  // promoción ([OTF←LOG] / [OTF←TRANS]).
  const labelPart = label ? `[${label}]` : "";
  const right = [labelPart, speaker].filter(Boolean).join(" ");
  if (!right) return date;
  return date ? `${date}   ${right}` : right;
}

// Ancho fijo reservado para la columna del TC a la izquierda del cuerpo. El
// resto del contenido se envuelve dentro del ancho restante alineado a esa
// columna, dando un look de "stripe" tipográfico (TC bold | texto plano).
const TC_COL_W = 56;
const TC_GUTTER = 10;

function drawBodyWithTc(ctx: RenderCtx, tc: string, text: string): void {
  const tcTrim = tc.trim();
  if (!tcTrim) {
    drawText(ctx, text);
    return;
  }
  ctx.doc.setFont(FONT_FAMILY, "normal");
  ctx.doc.setFontSize(BODY_SIZE);
  ctx.doc.setTextColor(...BODY_COLOR);

  const bodyX = MARGIN + TC_COL_W + TC_GUTTER;
  const bodyW = PAGE_W - MARGIN - bodyX;
  const lines = text ? (ctx.doc.splitTextToSize(text, bodyW) as string[]) : [""];
  const lh = BODY_SIZE * 1.35;

  for (let i = 0; i < lines.length; i++) {
    ensureSpace(ctx, lh);
    if (i === 0) {
      ctx.doc.setFont(FONT_FAMILY, "bold");
      ctx.doc.text(tcTrim, MARGIN, ctx.y);
      ctx.doc.setFont(FONT_FAMILY, "normal");
    }
    if (lines[i]) ctx.doc.text(lines[i], bodyX, ctx.y);
    ctx.y += lh;
  }
}

// Tipos que se pueden colapsar con su antecesor cuando comparten cabecera —
// evita repetir "Jun 07  [LOG]" arriba de cada tarjeta del mismo grupo.
function isGroupableType(t: unknown): boolean {
  return t === BLOCK_LOG || t === BLOCK_OTF || t === BLOCK_TRANSCRIPT;
}

function groupKey(el: any): string {
  const date = formatDateLabel(pickDateSource(el));
  const speaker = el.speakerName ? String(el.speakerName) : "";
  if (el.type === BLOCK_OTF) {
    // OTF conserva su label en la cabecera, así que dos OTF con distinto origen
    // no deben colapsar.
    const origin = el.originType ? String(el.originType) : "";
    return `otf|${date}|${speaker}|${origin}`;
  }
  // LOG/TRANSCRIPT comparten cabecera (sin label) → mismo grupo "cuerpo".
  return `body|${date}|${speaker}`;
}

function drawBlock(
  ctx: RenderCtx,
  node: TElement,
  resolveStreamTags?: StreamTagResolver,
  groupedWithPrev = false,
): boolean {
  const text = nodeText(node);
  const tagLabels = resolveStreamTags ? resolveStreamTags(node) : [];
  const tagsLine = tagLabels.length ? `· ${tagLabels.join(" · ")}` : "";

  switch (node.type) {
    case BLOCK_LOG:
      if (!groupedWithPrev) drawText(ctx, blockHeaderText(null, node));
      if (tagsLine) drawText(ctx, tagsLine);
      drawBodyWithTc(ctx, (node as any).tc ? String((node as any).tc) : "", text);
      return true;
    case BLOCK_OTF:
      if (!groupedWithPrev)
        drawText(ctx, blockHeaderText(otfLabelWithOrigin((node as any).originType), node));
      if (tagsLine) drawText(ctx, tagsLine);
      drawBodyWithTc(ctx, (node as any).tc ? String((node as any).tc) : "", text);
      return true;
    case BLOCK_TRANSCRIPT:
      if (!groupedWithPrev) drawText(ctx, blockHeaderText(null, node));
      if (tagsLine) drawText(ctx, tagsLine);
      drawBodyWithTc(ctx, (node as any).tc ? String((node as any).tc) : "", text);
      return true;
    case BLOCK_PLECA:
      drawText(ctx, `PLECA: ${text}`, "bold");
      return true;
    case BLOCK_NOTA:
      if (!text) return false;
      drawText(ctx, `RECURSO: ${text}`, "bold");
      return true;
    case BLOCK_CHECKLIST: {
      drawText(ctx, "[CHECKLIST DE CIERRE]");
      const c = node as ChecklistBlockElement;
      for (const it of c.items) {
        drawText(ctx, `  [${it.done ? "x" : " "}] ${it.label}`);
      }
      return true;
    }
    case "h2":
      if (!text) return false;
      drawText(ctx, text.toUpperCase());
      return true;
    case "p":
    default:
      if (!text) return false;
      drawText(ctx, text);
      return true;
  }
}

export function buildExportPdf(
  project: AssemblyProject,
  titulo: string,
  sinopsis: string,
  doc: TElement[],
  autor: string,
  resolveStreamTags?: StreamTagResolver,
): jsPDF {
  const pdf = new jsPDF({ unit: "pt", format: "a4" });
  const ctx: RenderCtx = { doc: pdf, y: MARGIN };

  // Título (sigue siendo el único elemento con estilo propio — bold y un poco
  // más grande para que sea reconocible. El resto es texto plano).
  pdf.setFont(FONT_FAMILY, "bold");
  pdf.setFontSize(20);
  pdf.setTextColor(...BODY_COLOR);
  const tituloLines = pdf.splitTextToSize(titulo || "Sin título", CONTENT_W) as string[];
  for (const line of tituloLines) {
    pdf.text(line, MARGIN, ctx.y);
    ctx.y += 24;
  }
  ctx.y += 6;

  // Metadata — texto plano.
  const fecha = project.createdAt ? new Date(project.createdAt) : new Date();
  const fechaStr = fecha.toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "long",
  });
  // Fecha en cursiva, autor en negrita — sin labels.
  drawText(ctx, fechaStr, "italic");
  drawText(ctx, autor || "—", "bold");

  if (sinopsis?.trim()) {
    ctx.y += 6;
    drawText(ctx, sinopsis.trim(), "bold");
  }

  ctx.y += 6;
  drawDivider(ctx);

  // Bloques. Entre bloques distintos pintamos el divider completo; entre
  // bloques del mismo grupo (mismo tipo + fecha + speaker, contiguos) sólo
  // un aire chico y NO redibujamos la cabecera (ver drawBlock + groupKey).
  // Trackeamos `lastEmittedNode` (no el bloque previo crudo) para que un
  // bloque descartado en el medio — nota vacía, p.ej. — no rompa el grupo.
  const GROUP_PAD = 6;
  let lastEmittedNode: TElement | null = null;
  for (const node of doc) {
    const grouped =
      !!lastEmittedNode &&
      isGroupableType(node.type) &&
      isGroupableType(lastEmittedNode.type) &&
      groupKey(node) === groupKey(lastEmittedNode);
    if (lastEmittedNode) {
      if (grouped) {
        ctx.y += GROUP_PAD;
      } else {
        drawDivider(ctx);
      }
    }
    const drew = drawBlock(ctx, node, resolveStreamTags, grouped);
    if (drew) lastEmittedNode = node;
  }

  return pdf;
}

export function downloadPdf(filename: string, pdf: jsPDF): void {
  pdf.save(filename);
}

export { sanitizeFilename };
